export type ChatResponseChunk = {
    body: unknown;
    contentType: string;
    done?: boolean;
    status?: number;
    type: string;
};

export type ChatServiceConfiguration = {
    endpoint: string;
    feedbackEndpoint?: string;
    headers?: Record<string, string | undefined>;
    agentId?: string;
    getAuthorizationToken?: () => string | Promise<string>;
    onEnrichContext?: () => unknown | Promise<unknown>;
};

export type ChatStreamRequest = {
    message: string;
    signal?: AbortSignal;
    threadId?: string | null;
    images?: ChatImagePayload[];
};

export type ChatStreamChunk = ChatResponseChunk & {
    threadId?: string | null;
};

export type ChatImagePayload = {
    id?: string;
    fileName?: string;
    mimeType?: string;
    base64Body: string;
    sizeBytes?: number;
};

const NDJSON_MIME = 'application/x-ndjson';
const MAX_ENRICHED_CONTEXT_BYTES = 50 * 1024;
const DEFAULT_HEADERS = {
    Accept: NDJSON_MIME,
    'Content-Type': 'text/plain'
} as const;

export class ChatService {
    private readonly endpoint: string;
    private readonly headers: Record<string, string>;
    private readonly getAuthorizationToken?: () => string | Promise<string>;
    private readonly onEnrichContext?: () => unknown | Promise<unknown>;

    public constructor(configuration: ChatServiceConfiguration) {
        if (!configuration.endpoint?.trim()) {
            throw new Error('Chat endpoint must be provided.');
        }

        this.endpoint = configuration.endpoint.trim();
        this.headers = normalizeHeaders(configuration);
        this.getAuthorizationToken = configuration.getAuthorizationToken;
        this.onEnrichContext = configuration.onEnrichContext;
    }

    public async *stream(request: ChatStreamRequest): AsyncGenerator<ChatStreamChunk> {
        const { message, images, signal, threadId } = request;
        const normalizedMessage = message.trim();
        const hasImages = Array.isArray(images) && images.length > 0;

        if (!normalizedMessage.length && !hasImages) {
            return;
        }

        const headers = {
            ...this.headers
        };

        const authorizationToken = await resolveAuthorizationHeader(this.getAuthorizationToken);
        if (authorizationToken) {
            headers.Authorization = authorizationToken;
        }

        if (threadId?.trim()) {
            headers['thread-id'] = threadId.trim();
        }

        let payload: string;

        const enrichedContext = await resolveEnrichedContext(this.onEnrichContext);
        if (hasImages || enrichedContext) {
            headers['Content-Type'] = 'application/json';

            const payloadBody: {
                text: string;
                images?: ChatImagePayload[];
                enrichedContext?: Record<string, unknown>;
            } = {
                text: normalizedMessage,
                images
            };

            if (enrichedContext) {
                payloadBody.enrichedContext = enrichedContext;
            }

            payload = JSON.stringify(payloadBody);
        }
        else {
            payload = normalizedMessage;
        }

        const response = await fetch(this.endpoint, {
            method: 'POST',
            headers,
            body: payload,
            signal
        });

        if (!response.ok) {
            const errorText = await safeReadText(response);
            throw new Error(errorText || `Request failed with status ${response.status}.`);
        }

        const contentType = response.headers.get('content-type') ?? '';
        const resolvedCountry = response.headers.get('x-resolved-country');
        if (resolvedCountry) {
            console.info(`[widget] Resolved country: ${resolvedCountry}`);
        }

        const responseThreadId = response.headers.get('thread-id');

        if (contentType.includes(NDJSON_MIME) && response.body) {
            for await (const chunk of parseNdjsonStream(response.body, responseThreadId)) {
                yield chunk;
            }

            return;
        }

        const fallbackText = await safeReadText(response);
        if (fallbackText) {
            yield createTextChunk(fallbackText, response.status, responseThreadId);
        }
    }
}

async function* parseNdjsonStream(stream: ReadableStream<Uint8Array>, threadId?: string | null): AsyncGenerator<ChatStreamChunk> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { value, done } = await reader.read();

            if (done) {
                const trimmed = buffer.trim();
                if (trimmed.length > 0) {
                    const chunk = safeParseChunk(trimmed);
                    if (chunk) {
                        yield withThreadId(chunk, threadId);
                    }
                }

                break;
            }

            buffer += decoder.decode(value, { stream: true });
            let newlineIndex = buffer.indexOf('\n');

            while (newlineIndex >= 0) {
                const line = buffer.slice(0, newlineIndex).trim();
                buffer = buffer.slice(newlineIndex + 1);

                if (line.length > 0) {
                    const chunk = safeParseChunk(line);
                    if (chunk) {
                        yield withThreadId(chunk, threadId);
                    }
                }

                newlineIndex = buffer.indexOf('\n');
            }
        }
    } finally {
        reader.releaseLock();
    }
}

function safeParseChunk(payload: string): ChatResponseChunk | null {
    try {
        return JSON.parse(payload) as ChatResponseChunk;
    } catch {
        return null;
    }
}

function createTextChunk(body: string, status?: number, threadId?: string | null): ChatStreamChunk {
    return {
        body,
        contentType: 'text',
        done: true,
        status,
        type: 'text',
        threadId
    };
}

function withThreadId(chunk: ChatResponseChunk, threadId?: string | null): ChatStreamChunk {
    return {
        ...chunk,
        threadId
    };
}

async function safeReadText(response: Response): Promise<string> {
    try {
        return await response.text();
    } catch {
        return '';
    }
}

function normalizeHeaders(configuration: ChatServiceConfiguration): Record<string, string> {
    const normalized: Record<string, string> = {
        ...DEFAULT_HEADERS
    };

    Object.entries(configuration.headers ?? {}).forEach(([key, value]) => {
        if (!value) {
            return;
        }

        if (key.trim().toLowerCase() === 'authorization') {
            return;
        }

        normalized[key] = value;
    });

    if (configuration.agentId && !normalized['agent-id'] && !normalized['agentId']) {
        normalized['agent-id'] = configuration.agentId;
    }

    return normalized;
}

async function resolveAuthorizationHeader(
    getAuthorizationToken?: () => string | Promise<string>
): Promise<string | undefined> {
    if (!getAuthorizationToken) {
        return undefined;
    }

    try {
        const token = await getAuthorizationToken();
        const normalizedToken = normalizeAuthorizationToken(token);
        return normalizedToken;
    } catch {
        return undefined;
    }
}

function normalizeAuthorizationToken(token: string): string | undefined {
    const trimmedToken = token?.trim();
    if (!trimmedToken) {
        return undefined;
    }

    return trimmedToken.startsWith('Bearer ') ? trimmedToken : `Bearer ${trimmedToken}`;
}

async function resolveEnrichedContext(
    onEnrichContext?: () => unknown | Promise<unknown>
): Promise<Record<string, unknown> | undefined> {
    if (!onEnrichContext) {
        return undefined;
    }

    let candidate: unknown;
    try {
        candidate = await onEnrichContext();
    } catch (error) {
        console.warn('[widget] Failed to resolve enriched context. Sending message without enrichedContext.', error);
        return undefined;
    }

    if (typeof candidate === 'string') {
        const parsedStringContext = tryParseJsonObject(candidate);
        if (!parsedStringContext) {
            console.warn('[widget] Enriched context callback returned invalid JSON. Sending message without enrichedContext.');
            return undefined;
        }

        candidate = parsedStringContext;
    }

    if (!isPlainObject(candidate)) {
        console.warn('[widget] Enriched context must be a JSON object. Sending message without enrichedContext.');
        return undefined;
    }

    let serialized: string;
    try {
        serialized = JSON.stringify(candidate);
    } catch (error) {
        console.warn('[widget] Failed to serialize enriched context. Sending message without enrichedContext.', error);
        return undefined;
    }

    if (!serialized || serialized === '{}') {
        return undefined;
    }

    const parsed = tryParseJsonObject(serialized);

    if (!parsed) {
        console.warn('[widget] Enriched context must serialize to a valid JSON object. Sending message without enrichedContext.');
        return undefined;
    }

    const utf8ByteCount = new TextEncoder().encode(serialized).length;
    if (utf8ByteCount > MAX_ENRICHED_CONTEXT_BYTES) {
        console.warn(
            `[widget] Enriched context exceeds ${MAX_ENRICHED_CONTEXT_BYTES} bytes and will be omitted from the request.`
        );
        return undefined;
    }

    return parsed;
}

function tryParseJsonObject(payload: string): Record<string, unknown> | undefined {
    try {
        const parsed: unknown = JSON.parse(payload);
        return isPlainObject(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
